import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { SidebarIdContent } from '@jira/navigation-apps__constants/src/SidebarIdContent.tsx';
import { AsyncHorizontalNavJira } from '@jira/navigation-apps__horizontal-nav-jira/src/async.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { genericProjectLayout } from '@jira/platform__layouts-generic-project-layout/src/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { createLayout } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import { issueNavigatorProjectClassicRoute } from '@jira/router-routes__issue-navigator-routes/src/issueNavigatorProjectClassicRoute.tsx';
import { ProjectIssueNavigatorSkeleton } from '@jira/platform__skeletons/src/ui/issue-navigator/components/project-app.tsx';
import { NIN_NEW_ISSUE_NAVIGATOR } from '@jira/platform__observability-capabilities/src/constants.tsx';
import type { Route } from '@atlassian/react-resource-router';
import { getCommonProjectResources } from './common/getCommonProjectResources.tsx';
import { getRouteReporting } from './common/getRouteReporting.tsx';
import { getSsrCircuitBreaker } from './common/getSsrCircuitBreaker.tsx';
import { adjustSsrFetchTimeout } from './common/getSsrFetchTimeout.tsx';
import {
	GSNIssueNavigatorPageEntryPointWithNested,
	GSNIssueNavigatorPageEntryPointWithNestedWithoutPreviewPanel,
	issueNavigatorPageEntryPointWithNested,
	issueNavigatorPageEntryPointWithNestedWithoutPreviewPanel,
} from './ui/index.tsx';
import { ListRedirect } from './ui/list-redirect/ListRedirect.tsx';

/**
 * Classic Project Issue Navigator Route Entry
 * Handles Classic projects, also known as company-managed projects.
 */
export const issueNavigatorProjectClassicRouteEntry: Route = createEntry(
	issueNavigatorProjectClassicRoute,
	() => ({
		group: 'issue-navigator',
		meta: {
			capability: NIN_NEW_ISSUE_NAVIGATOR,
			reporting: getRouteReporting(),
		},
		skeleton: ProjectIssueNavigatorSkeleton,
		navigation: {
			menuId: MenuIdProjects,
			horizontal: AsyncHorizontalNavJira,
			sidebarId: SidebarIdContent,
		},
		resources: [...getCommonProjectResources({ isUsingSidebarEntrypoint: true })],
		layout: createLayout(genericProjectLayout),
		isHydrateRootEnabled: true,
		bifrost: {
			bifrostGroup: 'issue-navigator',
			manifestName: 'jira-spa-issue-navigator.json',
			// Experiment to test the impact of increasing the resource timeout for issue navigator routes with more performance SSR
			fetchTimeout: adjustSsrFetchTimeout(expVal('nin_ssr_resource_timeout', 'timeout', 0)),
			circuitBreakerConfig: getSsrCircuitBreaker(),
		},

		// eslint-disable-next-line no-restricted-syntax
		component: fg('jira_list_nin_merge') ? ListRedirect : undefined,
		entryPoint: () => {
			if (fg('jira_list_nin_merge')) {
				return undefined;
			}
			const withPreviewPanel = fg('navx-global-side-nav')
				? GSNIssueNavigatorPageEntryPointWithNested
				: issueNavigatorPageEntryPointWithNested;
			const withoutPreviewPanel = fg('navx-global-side-nav')
				? GSNIssueNavigatorPageEntryPointWithNestedWithoutPreviewPanel
				: issueNavigatorPageEntryPointWithNestedWithoutPreviewPanel;
			return fg('nin_glance_panel_technical_changes') ? withPreviewPanel : withoutPreviewPanel;
		},
		supportsPreviewPanel: true,
		supportsProjectTheme: expVal('jira-list-full-theming-exp', 'isEnabled', false)
			? 'full'
			: 'contoured',
		excludeFromWrmDeferredLoading: true,
	}),
);
